import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { invokeApi } from "src/bl_libs/invokeApi";

/** Must match backend VERIFY_KEY. Temporary live check only — not linked in nav. */
const VERIFY_KEY = "bms-pr-live";

const runVerify = () =>
  invokeApi({
    path: `/api/testing/verify_progress_report`,
    method: "GET",
    queryParams: { key: VERIFY_KEY },
  });

/**
 * Hit this URL after a live deploy to confirm:
 * 1) AI wording works (key / provider)
 * 2) PDF generates with the BMS logo
 * 3) Email with PDF arrives at the test inbox
 *
 * No menu link — open directly:
 *   /verify-progress-report
 */
export default function VerifyProgressReportLive() {
  const [state, setState] = useState("loading");
  const [result, setResult] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [error, setError] = useState("");
  const pdfUrlRef = useRef("");

  const setPdfBlobUrl = (nextUrl) => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
    }
    pdfUrlRef.current = nextUrl || "";
    setPdfUrl(pdfUrlRef.current);
  };

  const run = async () => {
    setState("loading");
    setError("");
    setResult(null);
    setPdfBlobUrl("");
    try {
      const res = await runVerify();
      if (res?.code !== 200 || !res?.pdfBase64) {
        setError(res?.message || "Verify failed");
        setState("error");
        return;
      }
      const bytes = Uint8Array.from(atob(res.pdfBase64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: "application/pdf" });
      setPdfBlobUrl(URL.createObjectURL(blob));
      setResult(res);
      setState("ready");
    } catch (err) {
      setError(err?.message || "Verify failed");
      setState("error");
    }
  };

  useEffect(() => {
    run();
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = "";
      }
    };
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#0f172a", color: "#fff", p: 2 }}>
      <Box sx={{ maxWidth: 960, mx: "auto" }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>
          Live progress-report verify
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#94a3b8", mb: 2 }}>
          Dummy data · AI wording · PDF preview · email to test inbox. Not linked
          in the app menu.
        </Typography>

        {state === "loading" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 4 }}>
            <CircularProgress size={22} sx={{ color: "#22d3ee" }} />
            <Typography>Generating AI report and sending email…</Typography>
          </Box>
        ) : null}

        {state === "error" ? (
          <Box sx={{ py: 2 }}>
            <Typography sx={{ color: "#f87171", mb: 2 }}>{error}</Typography>
            <Button variant="contained" onClick={run}>
              Retry
            </Button>
          </Box>
        ) : null}

        {state === "ready" && result ? (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 0.5 }}>
              AI used:{" "}
              <strong style={{ color: result.usedAi ? "#4ade80" : "#fbbf24" }}>
                {result.usedAi ? "YES" : "NO (template fallback)"}
              </strong>
            </Typography>
            <Typography sx={{ mb: 0.5, fontSize: 13, color: "#cbd5e1" }}>
              Wording source: {result.wordingSource || "—"}
            </Typography>
            <Typography sx={{ mb: 0.5, fontSize: 13, color: "#cbd5e1" }}>
              Provider: {result.resolvedProvider || result.activeProvider || "—"}
            </Typography>
            <Typography sx={{ mb: 0.5 }}>
              Email:{" "}
              <strong style={{ color: result.emailSent ? "#4ade80" : "#f87171" }}>
                {result.emailSent
                  ? `Sent to ${result.emailTo}`
                  : `Failed — ${result.emailError || "unknown"}`}
              </strong>
            </Typography>
            {result.headline ? (
              <Typography sx={{ mt: 1.5, fontSize: 13, color: "#e2e8f0" }}>
                Headline: {result.headline}
              </Typography>
            ) : null}
            <Button variant="outlined" onClick={run} sx={{ mt: 2, color: "#fff", borderColor: "#64748b" }}>
              Run again
            </Button>
          </Box>
        ) : null}

        {pdfUrl ? (
          <Box
            component="iframe"
            title="Progress report PDF"
            src={pdfUrl}
            sx={{
              width: "100%",
              height: "75vh",
              border: "1px solid #334155",
              borderRadius: 1,
              bgcolor: "#fff",
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
}
